import { useState } from 'react';
import { signInWithPopup, signOut } from 'firebase/auth';
import { ref, uploadBytes, listAll, deleteObject, getDownloadURL } from 'firebase/storage';
import { doc, setDoc, deleteDoc, getDoc } from 'firebase/firestore';
import { auth, provider, storage, db } from './firebaseConfig';

function hashPin(pin) {
  const encoder = new TextEncoder();
  return crypto.subtle.digest('SHA-256', encoder.encode(pin.trim())).then((buf) =>
    Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('')
  );
}

function base64url(str) {
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

export default function App() {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState('');
  const [uploading, setUploading] = useState(false);
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [currentPath, setCurrentPath] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pendingFile, setPendingFile] = useState(null);
  const [fileProtection, setFileProtection] = useState({});
  const [dragItem, setDragItem] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [showFolderDialog, setShowFolderDialog] = useState(false);
  const [pinDialog, setPinDialog] = useState({ open: false, fileName: '' });
  const [pinDialogValue, setPinDialogValue] = useState('');

  const username = user?.email?.split('@')[0] ?? '';

  const fetchFiles = async (name, path) => {
    const uname = name || username;
    if (!uname) return;
    const folderPath = path !== undefined ? path : currentPath;
    setLoadingFiles(true);
    try {
      const prefix = folderPath ? `${uname}/${folderPath}/` : `${uname}/`;
      const listRef = ref(storage, prefix);
      const result = await listAll(listRef);

      const fileNames = result.items
        .map((item) => item.name)
        .filter((name) => name !== '.keep');
      setFiles(fileNames);

      const subfolders = result.prefixes.map((p) => p.name);
      setFolders(subfolders);

      // Check protection status for each file
      const protectionStatus = {};
      await Promise.all(
        fileNames.map(async (fileName) => {
          const storagePath = folderPath
            ? `${uname}/${folderPath}/${fileName}`
            : `${uname}/${fileName}`;
          const newDocId = base64url(storagePath);
          const legacyDocId = storagePath.replace(/\//g, '_');

          try {
            let docSnap = await getDoc(doc(db, 'protectedFiles', newDocId));
            if (!docSnap.exists() && newDocId !== legacyDocId) {
              docSnap = await getDoc(doc(db, 'protectedFiles', legacyDocId));
            }
            protectionStatus[fileName] = docSnap.exists();
          } catch {
            // Permission denied means doc exists but belongs to another user,
            // or doc doesn't exist (Firestore rules can't read null resource.data)
            protectionStatus[fileName] = false;
          }
        })
      );
      setFileProtection(protectionStatus);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingFiles(false);
    }
  };

  const navigateTo = (path) => {
    setCurrentPath(path);
    setFiles([]);
    setFolders([]);
    fetchFiles(username, path);
  };

  const handleDelete = async (fileName) => {
    if (!window.confirm(`Delete ${fileName}?`)) return;
    try {
      const storagePath = currentPath
        ? `${username}/${currentPath}/${fileName}`
        : `${username}/${fileName}`;
      const fileRef = ref(storage, storagePath);
      await deleteObject(fileRef);
      // Clean up PIN docs (try both new and legacy docIds)
      const newDocId = base64url(storagePath);
      const legacyDocId = storagePath.replace(/\//g, '_');
      await deleteDoc(doc(db, 'protectedFiles', newDocId)).catch(() => {});
      if (newDocId !== legacyDocId) {
        await deleteDoc(doc(db, 'protectedFiles', legacyDocId)).catch(() => {});
      }
      setStatus(`Deleted ${fileName}`);
      fetchFiles(username, currentPath);
    } catch (err) {
      setStatus(`Delete failed: ${err.message}`);
    }
  };

  const handleTogglePin = async (fileName) => {
    const storagePath = currentPath
      ? `${username}/${currentPath}/${fileName}`
      : `${username}/${fileName}`;
    const docId = base64url(storagePath);
    const docRef = doc(db, 'protectedFiles', docId);

    if (fileProtection[fileName]) {
      // Remove protection (clean up both new and legacy)
      if (!window.confirm(`Remove PIN protection from ${fileName}?`)) return;
      await deleteDoc(docRef);
      const legacyDocId = storagePath.replace(/\//g, '_');
      if (docId !== legacyDocId) {
        await deleteDoc(doc(db, 'protectedFiles', legacyDocId)).catch(() => {});
      }
      setFileProtection((prev) => ({ ...prev, [fileName]: false }));
      setStatus(`PIN removed from ${fileName}`);
    } else {
      // Open PIN dialog
      setPinDialogValue('');
      setPinDialog({ open: true, fileName });
    }
  };

  const handlePinSubmit = async () => {
    const pin = pinDialogValue.trim();
    if (!pin) return;
    const fileName = pinDialog.fileName;
    const storagePath = currentPath
      ? `${username}/${currentPath}/${fileName}`
      : `${username}/${fileName}`;
    const docId = base64url(storagePath);
    const docRef = doc(db, 'protectedFiles', docId);
    const pinHash = await hashPin(pin);
    await setDoc(docRef, { pinHash, owner: username });
    setFileProtection((prev) => ({ ...prev, [fileName]: true }));
    setStatus(`PIN set for ${fileName}`);
    setPinDialog({ open: false, fileName: '' });
    setPinDialogValue('');
  };

  const handleSignIn = async () => {
    try {
      const result = await signInWithPopup(auth, provider);
      setUser(result.user);
      fetchFiles(result.user.email.split('@')[0], '');
    } catch (err) {
      setStatus(`Sign-in failed: ${err.message}`);
    }
  };

  const handleSignOut = async () => {
    await signOut(auth);
    setUser(null);
    setStatus('');
    setCurrentPath('');
    setFiles([]);
    setFolders([]);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // Reset so the same file can be re-selected
    e.target.value = '';

    if (!file.name.endsWith('.html')) {
      setStatus('Only .html files are allowed.');
      return;
    }

    setPinInput('');
    setPendingFile(file);
  };

  const handleUpload = async (withPin) => {
    if (!pendingFile) return;

    const file = pendingFile;
    const pin = pinInput.trim();
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '-');

    setPendingFile(null);
    setPinInput('');
    setUploading(true);
    setStatus('Uploading...');

    try {
      const storagePath = currentPath
        ? `${username}/${currentPath}/${safeName}`
        : `${username}/${safeName}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file, { contentType: 'text/html' });

      if (withPin && pin) {
        const docId = base64url(storagePath);
        const pinHash = await hashPin(pin);
        await setDoc(doc(db, 'protectedFiles', docId), { pinHash, owner: username });
      }

      const urlPath = currentPath
        ? `${username}/${currentPath}/${safeName}`
        : `${username}/${safeName}`;
      const url = `${window.location.origin}/${urlPath}`;
      setStatus(`Uploaded! Share this URL with your client: ${url}`);
      fetchFiles(username, currentPath);
    } catch (err) {
      console.error(err);
      setStatus(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleCreateFolder = async () => {
    const name = newFolderName.trim().replace(/[^a-zA-Z0-9._-]/g, '-');
    if (!name) return;

    try {
      const keepPath = currentPath
        ? `${username}/${currentPath}/${name}/.keep`
        : `${username}/${name}/.keep`;
      const keepRef = ref(storage, keepPath);
      await uploadBytes(keepRef, new Uint8Array(0));
      setNewFolderName('');
      setStatus(`Folder "${name}" created`);
      fetchFiles(username, currentPath);
    } catch (err) {
      setStatus(`Failed to create folder: ${err.message}`);
    }
  };

  const listAllRecursive = async (storageRef) => {
    const result = await listAll(storageRef);
    let allItems = [...result.items];
    for (const prefix of result.prefixes) {
      const subItems = await listAllRecursive(prefix);
      allItems = allItems.concat(subItems);
    }
    return allItems;
  };

  // targetPath is relative to username, e.g. "" for root, "folder/sub" for nested
  const moveFileToPath = async (fileName, targetPath) => {
    const srcPath = currentPath
      ? `${username}/${currentPath}/${fileName}`
      : `${username}/${fileName}`;
    const destPath = targetPath
      ? `${username}/${targetPath}/${fileName}`
      : `${username}/${fileName}`;

    try {
      setStatus(`Moving ${fileName}...`);
      const srcRef = ref(storage, srcPath);
      const url = await getDownloadURL(srcRef);
      const response = await fetch(url);
      const blob = await response.blob();
      const destRef = ref(storage, destPath);
      await uploadBytes(destRef, blob, { contentType: 'text/html' });
      await deleteObject(srcRef);
    } catch (err) {
      setStatus(`Move failed: ${err.message}`);
      return;
    }

    // Move PIN doc if file is protected (best-effort, don't block on failure)
    if (fileProtection[fileName]) {
      const oldDocId = base64url(srcPath);
      const oldLegacyDocId = srcPath.replace(/\//g, '_');
      const newDocId = base64url(destPath);

      let pinData = null;
      try {
        const oldSnap = await getDoc(doc(db, 'protectedFiles', oldDocId));
        if (oldSnap.exists()) {
          pinData = oldSnap.data();
          await deleteDoc(doc(db, 'protectedFiles', oldDocId));
        }
      } catch (_) {}
      if (!pinData) {
        try {
          const legacySnap = await getDoc(doc(db, 'protectedFiles', oldLegacyDocId));
          if (legacySnap.exists()) {
            pinData = legacySnap.data();
            await deleteDoc(doc(db, 'protectedFiles', oldLegacyDocId));
          }
        } catch (_) {}
      }
      if (pinData) {
        try {
          await setDoc(doc(db, 'protectedFiles', newDocId), pinData);
        } catch (_) {}
      }
    }

    setStatus(`Moved ${fileName} to /${targetPath || username}/`);
    fetchFiles(username, currentPath);
  };

  const moveFolderToPath = async (folderName, targetPath) => {
    const srcPrefix = currentPath
      ? `${username}/${currentPath}/${folderName}`
      : `${username}/${folderName}`;
    const destPrefix = targetPath
      ? `${username}/${targetPath}/${folderName}`
      : `${username}/${folderName}`;

    try {
      setStatus(`Moving folder ${folderName}...`);
      const srcRef = ref(storage, srcPrefix + '/');
      const allItems = await listAllRecursive(srcRef);

      for (const item of allItems) {
        const fullPath = item.fullPath;
        const relativePath = fullPath.slice(srcPrefix.length + 1);
        const destPath = `${destPrefix}/${relativePath}`;

        const itemUrl = await getDownloadURL(item);
        const itemResponse = await fetch(itemUrl);
        const itemBlob = await itemResponse.blob();
        const contentType = item.name.endsWith('.html') ? 'text/html' : 'application/octet-stream';
        await uploadBytes(ref(storage, destPath), itemBlob, { contentType });
        await deleteObject(item);

        // Best-effort PIN doc move
        if (item.name.endsWith('.html')) {
          try {
            const oldDocId = base64url(fullPath);
            const newDocId = base64url(destPath);
            const oldSnap = await getDoc(doc(db, 'protectedFiles', oldDocId));
            if (oldSnap.exists()) {
              await setDoc(doc(db, 'protectedFiles', newDocId), oldSnap.data());
              await deleteDoc(doc(db, 'protectedFiles', oldDocId));
            } else {
              const oldLegacyDocId = fullPath.replace(/\//g, '_');
              const legacySnap = await getDoc(doc(db, 'protectedFiles', oldLegacyDocId));
              if (legacySnap.exists()) {
                await setDoc(doc(db, 'protectedFiles', newDocId), legacySnap.data());
                await deleteDoc(doc(db, 'protectedFiles', oldLegacyDocId));
              }
            }
          } catch (_) {}
        }
      }
    } catch (err) {
      setStatus(`Move failed: ${err.message}`);
      fetchFiles(username, currentPath);
      return;
    }

    setStatus(`Moved folder ${folderName} to /${targetPath || username}/`);
    fetchFiles(username, currentPath);
  };

  const handleDragStart = (type, name) => (e) => {
    setDragItem({ type, name });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (folderName) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTarget(folderName);
  };

  const handleDragLeave = () => {
    setDropTarget(null);
  };

  const handleDrop = (targetPath) => (e) => {
    e.preventDefault();
    setDropTarget(null);
    if (!dragItem) return;
    // Don't drop onto itself
    if (targetPath === currentPath) { setDragItem(null); return; }

    if (dragItem.type === 'file') {
      moveFileToPath(dragItem.name, targetPath);
    } else if (dragItem.type === 'folder') {
      // Don't drop a folder into itself
      if (targetPath === (currentPath ? `${currentPath}/${dragItem.name}` : dragItem.name)) {
        setDragItem(null);
        return;
      }
      moveFolderToPath(dragItem.name, targetPath);
    }
    setDragItem(null);
  };

  const handleDragEnd = () => {
    setDragItem(null);
    setDropTarget(null);
  };

  const handleDeleteFolder = async (folderName) => {
    const folderPath = currentPath
      ? `${username}/${currentPath}/${folderName}`
      : `${username}/${folderName}`;
    const folderRef = ref(storage, folderPath + '/');
    const result = await listAll(folderRef);

    // Check if folder has any real files (non-.keep)
    const realFiles = result.items.filter((item) => item.name !== '.keep');
    if (realFiles.length > 0 || result.prefixes.length > 0) {
      setStatus(`Cannot delete "${folderName}" — folder is not empty.`);
      return;
    }

    if (!window.confirm(`Delete folder "${folderName}"?`)) return;

    try {
      // Delete the .keep placeholder(s)
      for (const item of result.items) {
        await deleteObject(item);
      }
      setStatus(`Deleted folder "${folderName}"`);
      fetchFiles(username, currentPath);
    } catch (err) {
      setStatus(`Delete failed: ${err.message}`);
    }
  };

  const breadcrumbSegments = currentPath ? currentPath.split('/') : [];

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <img src="/logo.png" alt="ProfitOptics" style={styles.logo} />
        <h1 style={styles.title}>ProfitOptics Canvas Portal</h1>

        {!user ? (
          <>
            <p style={styles.description}>
              A simple tool for ProfitOptics team members to share HTML prototypes with clients. Build a prototype using an AI coding tool like Claude Code, Gemini, or ChatGPT, then upload and share it with a single link.
            </p>
            <button onClick={handleSignIn} style={styles.signInButton}>
              Sign in with Google
            </button>
            <p style={styles.domainNote}>Requires a @profitoptics.com account</p>
          </>
        ) : (
          <>
            <p style={styles.subtitle}>Signed in as <strong>{user.email}</strong></p>

            <label style={styles.uploadLabel}>
              {uploading ? 'Uploading...' : 'Choose HTML file to upload'}
              <input
                type="file"
                accept=".html"
                onChange={handleFileSelect}
                disabled={uploading}
                style={styles.fileInput}
              />
            </label>

            {pendingFile && (
              <div style={styles.modalOverlay} onClick={() => setPendingFile(null)}>
                <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                  <div style={styles.modalHeader}>
                    <h3 style={styles.modalTitle}>Upload {pendingFile.name}</h3>
                    <button onClick={() => setPendingFile(null)} style={styles.modalClose}>
                      &times;
                    </button>
                  </div>
                  <p style={styles.modalHint}>
                    Optionally set a PIN to restrict access. Share the PIN separately with your intended audience.
                  </p>
                  <input
                    type="text"
                    placeholder="Enter PIN (optional)"
                    value={pinInput}
                    onChange={(e) => setPinInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && pinInput.trim()) handleUpload(true);
                    }}
                    autoFocus
                    style={styles.folderDialogInput}
                  />
                  <div style={styles.uploadDialogActions}>
                    <button
                      onClick={() => handleUpload(true)}
                      disabled={!pinInput.trim()}
                      style={{
                        ...styles.folderDialogCreate,
                        ...(pinInput.trim() ? {} : styles.buttonDisabled),
                      }}
                    >
                      Upload Protected
                    </button>
                    <button
                      onClick={() => handleUpload(false)}
                      style={styles.uploadPublicButton}
                    >
                      Upload Public
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div style={styles.helpSection}>
              <h3 style={styles.helpHeader}>How do I build an HTML mockup to share with a client?</h3>
              <p style={styles.helpText}>
                Use an AI coding tool such as Claude Code, Gemini, or ChatGPT. Include a requirement in your prompt that the output should be a single, self-contained HTML file.
              </p>
              <button
                onClick={() => setShowPrompt(true)}
                style={styles.examplePromptButton}
              >
                View example prompt with brand colors
              </button>
            </div>

            {showPrompt && (
              <div style={styles.modalOverlay} onClick={() => setShowPrompt(false)}>
                <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                  <div style={styles.modalHeader}>
                    <h3 style={styles.modalTitle}>Example Prompt</h3>
                    <button onClick={() => setShowPrompt(false)} style={styles.modalClose}>
                      &times;
                    </button>
                  </div>
                  <p style={styles.modalHint}>
                    Copy and paste this into Claude Code, ChatGPT, or Gemini. Edit the description to match your project.
                  </p>
                  <pre id="example-prompt" style={styles.promptBlock}>{`Create a single-page, self-contained HTML file for [describe your mockup here].

All styles, scripts, and assets must be inline — no external dependencies.

Use the following ProfitOptics brand color palette:

Primary (Deep Teal):
  50: #f0fdfa   100: #ccfbf1  200: #99f6e4
  300: #5eead4  400: #2dd4bf  500: #14b8a6
  600: #086664  700: #05413F  800: #043533
  900: #032928  950: #021a19

Accent (Lime Green):
  50: #fafde8   100: #f3facc  200: #e6f49f
  300: #d4ea68  400: #BAD868  500: #a5c455
  600: #7f9a3d  700: #607530  800: #4d5d2a
  900: #424f27  950: #222b11

Neutral: Charcoal #232323

Guidelines:
- Use Deep Teal (#05413F) for headers, nav, and primary actions.
- Use Lime Green (#BAD868) for accents, highlights, and CTAs.
- Use lighter teal shades for backgrounds and borders.
- Keep the design clean, modern, and professional.
- Make it fully responsive for mobile and desktop.`}</pre>
                  <button
                    onClick={() => {
                      const pre = document.getElementById('example-prompt');
                      if (pre) {
                        navigator.clipboard.writeText(pre.textContent);
                        setStatus('Prompt copied to clipboard!');
                        setShowPrompt(false);
                      }
                    }}
                    style={styles.copyButton}
                  >
                    Copy to Clipboard
                  </button>
                </div>
              </div>
            )}

            {showFolderDialog && (
              <div style={styles.modalOverlay} onClick={() => { setShowFolderDialog(false); setNewFolderName(''); }}>
                <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                  <div style={styles.modalHeader}>
                    <h3 style={styles.modalTitle}>New Folder</h3>
                    <button onClick={() => { setShowFolderDialog(false); setNewFolderName(''); }} style={styles.modalClose}>
                      &times;
                    </button>
                  </div>
                  <input
                    type="text"
                    placeholder="Folder name"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleCreateFolder();
                        setShowFolderDialog(false);
                      }
                    }}
                    autoFocus
                    style={styles.folderDialogInput}
                  />
                  <div style={styles.folderDialogActions}>
                    <button
                      onClick={() => { setShowFolderDialog(false); setNewFolderName(''); }}
                      style={styles.folderDialogCancel}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => { handleCreateFolder(); setShowFolderDialog(false); }}
                      style={styles.folderDialogCreate}
                    >
                      Create
                    </button>
                  </div>
                </div>
              </div>
            )}

            {pinDialog.open && (
              <div style={styles.modalOverlay} onClick={() => { setPinDialog({ open: false, fileName: '' }); setPinDialogValue(''); }}>
                <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                  <div style={styles.modalHeader}>
                    <h3 style={styles.modalTitle}>Set PIN for {pinDialog.fileName}</h3>
                    <button onClick={() => { setPinDialog({ open: false, fileName: '' }); setPinDialogValue(''); }} style={styles.modalClose}>
                      &times;
                    </button>
                  </div>
                  <p style={styles.modalHint}>
                    Clients will need this PIN to view the file. Share it separately (e.g. via email or chat).
                  </p>
                  <input
                    type="text"
                    placeholder="Enter PIN"
                    value={pinDialogValue}
                    onChange={(e) => setPinDialogValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handlePinSubmit();
                    }}
                    autoFocus
                    style={styles.folderDialogInput}
                  />
                  <div style={styles.folderDialogActions}>
                    <button
                      onClick={() => { setPinDialog({ open: false, fileName: '' }); setPinDialogValue(''); }}
                      style={styles.folderDialogCancel}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handlePinSubmit}
                      style={styles.folderDialogCreate}
                    >
                      Set PIN
                    </button>
                  </div>
                </div>
              </div>
            )}

{status && <p style={styles.status}>{status}</p>}

            <div style={styles.fileSection}>
              <h3 style={styles.fileHeader}>
                {currentPath ? `Contents of /${currentPath}/` : 'Your Files'}
                <button
                  onClick={() => setShowFolderDialog(true)}
                  style={styles.createFolderBtn}
                  title="New folder"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                    <line x1="12" y1="11" x2="12" y2="17"/>
                    <line x1="9" y1="14" x2="15" y2="14"/>
                  </svg>
                </button>
              </h3>
              {/* Breadcrumb navigation */}
              {currentPath && (
                <div style={styles.breadcrumb}>
                  <span
                    onClick={() => navigateTo('')}
                    onDragOver={handleDragOver('__root__')}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop('')}
                    style={{
                      ...styles.breadcrumbLink,
                      ...(dropTarget === '__root__' ? styles.breadcrumbDropHighlight : {}),
                    }}
                  >
                    Home
                  </span>
                  {breadcrumbSegments.map((seg, i) => {
                    const path = breadcrumbSegments.slice(0, i + 1).join('/');
                    const isLast = i === breadcrumbSegments.length - 1;
                    return (
                      <span key={path}>
                        <span style={styles.breadcrumbSep}> / </span>
                        <span
                          onClick={() => !isLast && navigateTo(path)}
                          onDragOver={!isLast ? handleDragOver(path) : undefined}
                          onDragLeave={!isLast ? handleDragLeave : undefined}
                          onDrop={!isLast ? handleDrop(path) : undefined}
                          style={{
                            ...(isLast ? styles.breadcrumbCurrent : styles.breadcrumbLink),
                            ...(!isLast && dropTarget === path ? styles.breadcrumbDropHighlight : {}),
                          }}
                        >
                          {seg}
                        </span>
                      </span>
                    );
                  })}
                </div>
              )}
              {loadingFiles ? (
                <p style={styles.fileEmpty}>Loading...</p>
              ) : folders.length === 0 && files.length === 0 ? (
                <p style={styles.fileEmpty}>No files or folders here yet.</p>
              ) : (
                <ul style={styles.fileList}>
                  {folders.map((folderName) => {
                    const folderPath = currentPath
                      ? `${currentPath}/${folderName}`
                      : folderName;
                    const isOver = dropTarget === folderPath;
                    return (
                      <li
                        key={`folder-${folderName}`}
                        style={{
                          ...styles.fileItem,
                          ...(isOver ? styles.dropTargetHighlight : {}),
                        }}
                        draggable
                        onDragStart={handleDragStart('folder', folderName)}
                        onDragEnd={handleDragEnd}
                        onDragOver={handleDragOver(folderPath)}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop(folderPath)}
                      >
                        <div style={styles.fileNameRow}>
                          <span style={styles.folderIcon}>▸</span>
                          <span
                            onClick={() => navigateTo(folderPath)}
                            style={styles.folderLink}
                          >
                            {folderName}
                          </span>
                        </div>
                        <div style={styles.fileActions}>
                          <button
                            onClick={() => handleDeleteFolder(folderName)}
                            style={styles.deleteButton}
                          >
                            Delete
                          </button>
                        </div>
                      </li>
                    );
                  })}
                  {files.map((name) => {
                    const urlPath = currentPath
                      ? `${username}/${currentPath}/${name}`
                      : `${username}/${name}`;
                    return (
                      <li
                        key={name}
                        style={styles.fileItem}
                        draggable
                        onDragStart={handleDragStart('file', name)}
                        onDragEnd={handleDragEnd}
                      >
                        <div style={styles.fileNameRow}>
                          {fileProtection[name] && <span style={styles.lockIcon} title="PIN protected">&#128274;</span>}
                          <a
                            href={`${window.location.origin}/${urlPath}`}
                            target="_blank"
                            rel="noreferrer"
                            style={styles.fileLink}
                            draggable={false}
                          >
                            {name}
                          </a>
                        </div>
                        <div style={styles.fileActions}>
                          <button
                            onClick={() => handleTogglePin(name)}
                            style={fileProtection[name] ? styles.unlockButton : styles.lockButton}
                            title={fileProtection[name] ? 'Remove PIN' : 'Set PIN'}
                          >
                            {fileProtection[name] ? 'Unprotect' : 'Protect'}
                          </button>
                          <button
                            onClick={() => {
                              const url = `${window.location.origin}/${urlPath}`;
                              navigator.clipboard.writeText(url);
                              setStatus('URL copied to clipboard!');
                            }}
                            style={styles.copyUrlButton}
                          >
                            Copy URL
                          </button>
                          <button
                            onClick={() => handleDelete(name)}
                            style={styles.deleteButton}
                          >
                            Delete
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <button onClick={handleSignOut} style={styles.signOutButton}>
              Sign Out
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f0fdfa',
    fontFamily: 'sans-serif',
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '40px',
    width: '100%',
    maxWidth: '600px',
    boxShadow: '0 4px 20px rgba(5, 65, 63, 0.12)',
    textAlign: 'center',
  },
  logo: {
    width: '72px',
    height: '72px',
    marginBottom: '16px',
  },
  title: {
    fontSize: '24px',
    marginBottom: '24px',
    color: '#05413F',
  },
  subtitle: {
    color: '#232323',
    marginBottom: '4px',
  },
  description: {
    color: '#232323',
    fontSize: '15px',
    lineHeight: '1.6',
    marginBottom: '24px',
  },
  domainNote: {
    marginTop: '12px',
    fontSize: '13px',
    color: '#086664',
  },
  breadcrumb: {
    marginBottom: '16px',
    fontSize: '14px',
    color: '#05413F',
    textAlign: 'left',
    padding: '8px 12px',
    backgroundColor: '#f0fdfa',
    borderRadius: '6px',
  },
  breadcrumbLink: {
    color: '#086664',
    cursor: 'pointer',
    textDecoration: 'underline',
    padding: '2px 4px',
    borderRadius: '4px',
  },
  breadcrumbDropHighlight: {
    backgroundColor: '#ccfbf1',
    outline: '2px dashed #05413F',
    outlineOffset: '1px',
  },
  breadcrumbCurrent: {
    color: '#05413F',
    fontWeight: '600',
  },
  breadcrumbSep: {
    color: '#999',
  },
  signInButton: {
    padding: '12px 24px',
    backgroundColor: '#05413F',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    cursor: 'pointer',
  },
  uploadLabel: {
    display: 'block',
    padding: '12px 24px',
    backgroundColor: '#05413F',
    color: '#fff',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '600',
    marginBottom: '8px',
  },
  fileInput: {
    display: 'none',
  },
  uploadDialogActions: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end',
  },
  uploadPublicButton: {
    padding: '8px 16px',
    backgroundColor: 'transparent',
    color: '#086664',
    border: '1px solid #086664',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  buttonDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
  createFolderBtn: {
    background: 'none',
    border: 'none',
    color: '#05413F',
    cursor: 'pointer',
    marginLeft: '8px',
    padding: '2px',
    lineHeight: '0',
    verticalAlign: 'middle',
    display: 'inline-flex',
    alignItems: 'center',
  },
  folderDialogInput: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '2px solid #ccfbf1',
    borderRadius: '8px',
    outline: 'none',
    boxSizing: 'border-box',
    marginBottom: '16px',
  },
  folderDialogActions: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end',
  },
  folderDialogCancel: {
    padding: '8px 16px',
    backgroundColor: 'transparent',
    color: '#086664',
    border: '1px solid #086664',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  folderDialogCreate: {
    padding: '8px 16px',
    backgroundColor: '#05413F',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
  },
  status: {
    marginTop: '16px',
    fontSize: '14px',
    color: '#043533',
    wordBreak: 'break-all',
  },
  fileSection: {
    marginTop: '24px',
    textAlign: 'left',
  },
  fileHeader: {
    fontSize: '16px',
    color: '#05413F',
    marginBottom: '8px',
  },
  fileEmpty: {
    color: '#086664',
    fontSize: '14px',
  },
  fileList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  fileItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 4px',
    borderBottom: '1px solid #ccfbf1',
    borderRadius: '4px',
    transition: 'background-color 0.15s',
    cursor: 'grab',
  },
  dropTargetHighlight: {
    backgroundColor: '#ccfbf1',
    outline: '2px dashed #05413F',
    outlineOffset: '-2px',
  },
  fileNameRow: {
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    marginRight: '8px',
    flex: 1,
  },
  folderIcon: {
    fontSize: '16px',
    marginRight: '6px',
    flexShrink: 0,
  },
  folderLink: {
    color: '#05413F',
    fontWeight: '600',
    cursor: 'pointer',
    fontSize: '14px',
  },
  lockIcon: {
    fontSize: '14px',
    marginRight: '6px',
    flexShrink: 0,
  },
  fileLink: {
    color: '#086664',
    textDecoration: 'none',
    fontSize: '14px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  fileActions: {
    display: 'flex',
    gap: '6px',
    flexShrink: 0,
  },
  lockButton: {
    padding: '4px 10px',
    backgroundColor: 'transparent',
    color: '#05413F',
    border: '1px solid #05413F',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
  },
  unlockButton: {
    padding: '4px 10px',
    backgroundColor: '#05413F',
    color: '#fff',
    border: '1px solid #05413F',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
  },
  copyUrlButton: {
    padding: '4px 10px',
    backgroundColor: 'transparent',
    color: '#086664',
    border: '1px solid #086664',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
  },
  deleteButton: {
    padding: '4px 10px',
    backgroundColor: 'transparent',
    color: '#dc3545',
    border: '1px solid #dc3545',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
  },
  helpSection: {
    marginTop: '24px',
    padding: '16px',
    backgroundColor: '#fafde8',
    borderRadius: '8px',
    textAlign: 'left',
    borderLeft: '3px solid #BAD868',
  },
  helpHeader: {
    fontSize: '14px',
    color: '#05413F',
    marginTop: 0,
    marginBottom: '8px',
  },
  helpText: {
    fontSize: '13px',
    color: '#043533',
    margin: 0,
    lineHeight: '1.5',
  },
  examplePromptButton: {
    marginTop: '10px',
    padding: '6px 14px',
    backgroundColor: '#05413F',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '12px',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '28px',
    width: '90%',
    maxWidth: '560px',
    maxHeight: '80vh',
    overflow: 'auto',
    boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  modalTitle: {
    fontSize: '18px',
    color: '#05413F',
    margin: 0,
  },
  modalClose: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#666',
    padding: '0 4px',
  },
  modalHint: {
    fontSize: '13px',
    color: '#086664',
    marginBottom: '12px',
  },
  promptBlock: {
    backgroundColor: '#f0fdfa',
    border: '1px solid #ccfbf1',
    borderRadius: '8px',
    padding: '16px',
    fontSize: '12px',
    lineHeight: '1.6',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    color: '#043533',
    textAlign: 'left',
    margin: '0 0 16px 0',
  },
  copyButton: {
    width: '100%',
    padding: '10px',
    backgroundColor: '#05413F',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
  },
  signOutButton: {
    marginTop: '24px',
    padding: '8px 20px',
    backgroundColor: 'transparent',
    border: '1px solid #086664',
    borderRadius: '6px',
    cursor: 'pointer',
    color: '#086664',
    fontSize: '14px',
  },
};
